import * as L from "leaflet";
import { IBoundLayer, IBoundLayerOptions } from "../../../../map/BoundLayer";
import { feetToMeters } from "../../../../utils";
import config from "../../../../config";
import currentGcpRequestStore from "../../currentGCPRequestStore";
import { getGCPRequestState } from "../../../../redux/rootReducer";
import { ICoordinates } from "../../../../apiFacades/bapi/gcpRequests";
import { Black } from "@traceair/tds";
import { convertGlobalToWgs84Coords } from "../../../../utils/coordinateConversion/convertGlobalToWgs84Coords";

const circleProps = {
  fillColor: "transparent",
  fillOpacity: 1,
  weight: 4,
  interactive: false,
};

const tooltipProps = {
  permanent: true,
  interactive: false,
  opacity: 1,
};

const { minZoomLevelToShowBufferZones } = config.bufferZonesLayerOptions;

export default class FlightMarkersBufferZonesLayer
  extends L.LayerGroup
  implements IBoundLayer
{
  public options: IBoundLayerOptions;
  public map: L.Map;

  constructor(options: IBoundLayerOptions) {
    super();
    L.Util.setOptions(this, options);
  }

  public onAdd(map: L.Map) {
    L.LayerGroup.prototype.onAdd.call(this, map);
    this.map = map;
    this.map.on("zoomend", (e) => {
      this.update();
    });
    return this;
  }

  public onRemove(map: L.Map) {
    L.LayerGroup.prototype.onRemove.call(this, map);
    this.map.off("zoomstart");
    this.map.off("zoomend");
    this.map = null;

    return this;
  }

  public update() {
    // TODO: possible bottleneck, redraw only when necessary
    this.clearLayers();

    const currentGcpRequestStoreState = currentGcpRequestStore.getState();

    const currentGCPRequest = currentGcpRequestStoreState.GCPRequest;
    const site = currentGcpRequestStoreState.site;

    const suggestedFlightMarkers = currentGCPRequest?.suggested;
    const existingGcps = currentGCPRequest?.existing;
    const { installedFlightMarkers } = getGCPRequestState(
      this.options.getState(),
    );

    const hasSuggested = Boolean(suggestedFlightMarkers?.length);
    const hasExisting = Boolean(existingGcps?.length);
    const hasAnyGcps = hasSuggested || hasExisting;

    if (
      !hasAnyGcps ||
      this.map.getZoom() < minZoomLevelToShowBufferZones ||
      !site
    ) {
      return;
    }

    const { isImperial, srs } = site;

    const radius15ft = this.getRadiusLabel(15, isImperial);
    const radius30ft = this.getRadiusLabel(30, isImperial);

    const installedFlightMarkersIds = installedFlightMarkers.map(
      (marker) => marker.id,
    );

    // draw buffer areas only for not installed suggested flight markers
    suggestedFlightMarkers
      .filter((marker) => !installedFlightMarkersIds.includes(marker.id))
      .map((marker) => {
        const bufferZone15ft = this.createRadiusCircle(
          marker.coordinates,
          radius15ft.radiusInMeters,
          radius15ft.label,
          Black,
        );
        this.addLayer(bufferZone15ft);

        const bufferZone30ft = this.createRadiusCircle(
          marker.coordinates,
          radius30ft.radiusInMeters,
          radius30ft.label,
          "#414146", // non tds color?
        );
        this.addLayer(bufferZone30ft);

        const radiusLine = this.createRadiusLine(bufferZone30ft);
        this.addLayer(radiusLine);
      });

    const radiusExistingGcp = this.getRadiusLabel(50, isImperial);
    existingGcps.forEach((existingGcp) => {
      const radiusCircle = this.createRadiusCircle(
        convertGlobalToWgs84Coords(existingGcp.coordinates, srs),
        radiusExistingGcp.radiusInMeters,
        radiusExistingGcp.label,
        Black,
      );
      this.addLayer(radiusCircle);

      const radiusLine = this.createRadiusLine(radiusCircle);
      this.addLayer(radiusLine);
    });
  }

  private createRadiusCircle(
    coordinates: ICoordinates,
    radiusMeters: number,
    text: string,
    circleColor: string,
  ) {
    const { y: lat, x: lng } = coordinates;
    const radiusCircle = L.circle([lat, lng], {
      ...circleProps,
      color: circleColor,
      radius: radiusMeters,
    });
    radiusCircle.bindTooltip(text, {
      ...tooltipProps,
      direction: "center",
      className: "tooltip-buffer-zone buffer-zone-15ft",
      offset: new L.Point(radiusMeters / this.getMetersPerPixel(), 0),
    });
    return radiusCircle;
  }

  private getRadiusLabel(targetRadiusFt: number, isImperial: boolean) {
    const radiusInMeters = feetToMeters(targetRadiusFt);
    const label = !isImperial
      ? `~${Math.ceil(radiusInMeters)} m`
      : `${targetRadiusFt} ft`;

    return {
      radiusInMeters,
      label,
    };
  }

  private createRadiusLine(radiusCircle: L.Circle) {
    const dashedGuidedLineLayer = L.polyline(
      [
        radiusCircle.getLatLng(),
        new L.LatLng(
          radiusCircle.getLatLng().lat,
          radiusCircle.getBounds().getEast(),
        ),
      ],
      {
        color: "#000000",
        weight: 2,
        opacity: 1,
        dashArray: "2, 4",
        dashOffset: "0",
      },
    );
    return dashedGuidedLineLayer;
  }

  // Meter per pixel means ratio: map height in meters to map height in pixels
  private getMetersPerPixel() {
    const southEastPoint = this.map.getBounds().getSouthEast();
    const northEastPoint = this.map.getBounds().getNorthEast();
    const mapHeightInMetres = southEastPoint.distanceTo(northEastPoint);
    const mapHeightInPixels = this.map.getSize().y;

    return mapHeightInMetres / mapHeightInPixels;
  }
}
